import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  fetchCohorts,
  fetchDatasets,
  fetchExecutive,
  fetchExportLog,
  fetchForecast,
  fetchIdentification,
  fetchLocations,
  fetchMarts,
  fetchMetrics,
  fetchPolicy,
  fetchSubscriptions,
  refreshMarts,
  runExport,
  saveLocation,
  savePolicy,
  syncWeather,
  type CohortReport,
  type ForecastReport,
  type SiteLocation,
  type DatasetInfo,
  type ExecutiveReport,
  type ExportLogEntry,
  type IdentificationDashboard,
  type MartStatus,
  type MetricsResponse,
  type Policy,
  type SubscriptionReport,
} from './api';
import { makeAnalyticsT, money, percent, type AnalyticsKey } from './i18n';
import { downloadText, exportFilename, EXPORT_MIME } from '../../shell/download';
import { countWithNoun } from '../../shell/format';
import {
  Button,
  LoadingState,
  ModulePage,
  Segmented,
  StatusBadge,
  Table,
  Field,
  Select,
  Checkbox,
} from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { useTab } from '../../shell/hooks';

const PERIODS = [
  { key: 'day', days: 1, label: 'period.day' },
  { key: 'week', days: 7, label: 'period.week' },
  { key: 'month', days: 30, label: 'period.month' },
  { key: 'quarter', days: 90, label: 'period.quarter' },
] as const;

type PeriodKey = (typeof PERIODS)[number]['key'];

const TABS = [
  { key: 'identification', label: 'tab.identification', need: 'site' },
  { key: 'executive', label: 'tab.executive', need: 'site' },
  { key: 'cohorts', label: 'tab.cohorts', need: 'network' },
  { key: 'subscriptions', label: 'tab.subscriptions', need: 'network' },
  { key: 'metrics', label: 'tab.metrics', need: 'site' },
  { key: 'forecast', label: 'tab.forecast', need: 'site' },
  { key: 'policy', label: 'tab.policy', need: 'admin' },
  { key: 'exports', label: 'tab.exports', need: 'export' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'bad' }) {
  const color = tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="overflow-x-auto p-4">{children}</div>
    </section>
  );
}

// Ноль возвратов — не событие, нейтральный прочерк не отвлекает от строки.
// Ненулевой возврат — то, из-за чего валовая и чистая выручка расходятся, и
// раньше эта сумма нигде не показывалась: дашборд просто завышал выручку.
function refundCell(refundedMinor: number): React.ReactNode {
  if (refundedMinor <= 0) return <span className="text-ink-faint">—</span>;
  return <StatusBadge tone="warn">{money(refundedMinor)}</StatusBadge>;
}

function DataGrid({
  head,
  rows,
  empty,
}: {
  head: readonly string[];
  rows: readonly (readonly React.ReactNode[])[];
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-ink-muted">{empty}</p>;
  return (
    <Table>
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-ink-muted">
          {head.map((cell) => (
            <th key={cell} className="pb-2 pr-4 font-medium">
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} className="border-t border-line">
            {row.map((cell, cellIndex) => (
              <td key={cellIndex} className="py-2 pr-4 text-ink">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default function AnalyticsModule({ user, siteId, title }: ModuleViewProps) {
  const t = useMemo(() => makeAnalyticsT(), []);
  const tr = useCallback((key: AnalyticsKey) => t(key), [t]);
  const toast = useToast();
  const setNotice = toast.ok;

  const networkWide = user.bindings.some((b) => b.siteId === null);
  const roles = new Set(user.bindings.map((b) => b.role));
  const canAdmin = roles.has('network_admin');
  // Вкладку выгрузок открывает то же право, что и саму выгрузку (report.site):
  // маркетолог держит customer.export, а вкладку раньше не видел (QA.md, Q12).
  // Журнал выгрузок читается по audit.view — его грузим только тем, у кого оно есть.
  const canExport = user.permissionsAnySite.includes('report.site');
  const canReadExportLog = user.permissionsAnySite.includes('audit.view');

  const visible = TABS.filter((tab) => {
    if (tab.need === 'network') return networkWide;
    if (tab.need === 'admin') return canAdmin;
    if (tab.need === 'export') return canExport;
    return true;
  });

  const [tab, setTab] = useTab<TabKey>(
    'analytics',
    visible.map((item) => item.key),
    visible[0]?.key ?? 'identification',
  );
  const [periodKey, setPeriodKey] = useState<PeriodKey>('week');
  const [busy, setBusy] = useState(false);

  const [dashboard, setDashboard] = useState<IdentificationDashboard | null>(null);
  const [executive, setExecutive] = useState<ExecutiveReport | null>(null);
  const [cohorts, setCohorts] = useState<CohortReport | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionReport | null>(null);
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [datasets, setDatasets] = useState<readonly DatasetInfo[]>([]);
  const [log, setLog] = useState<readonly ExportLogEntry[]>([]);
  const [marts, setMarts] = useState<readonly MartStatus[]>([]);
  const [forecast, setForecast] = useState<ForecastReport | null>(null);
  const [location, setLocation] = useState<SiteLocation | null>(null);
  const [coords, setCoords] = useState({ latitude: '', longitude: '' });

  const period = useMemo(() => {
    const days = PERIODS.find((p) => p.key === periodKey)?.days ?? 7;
    const to = new Date();
    const from = new Date(to.getTime() - days * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [periodKey]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      if (tab === 'identification') {
        setDashboard(await fetchIdentification(period.from, period.to, siteId));
      } else if (tab === 'executive') {
        setExecutive(await fetchExecutive(period.from, period.to, siteId));
      } else if (tab === 'cohorts') {
        setCohorts(await fetchCohorts(period.from, period.to));
      } else if (tab === 'subscriptions') {
        setSubscriptions(await fetchSubscriptions(period.from, period.to));
      } else if (tab === 'metrics') {
        setMetrics(await fetchMetrics(period.from, period.to, siteId));
      } else if (tab === 'forecast') {
        setForecast(null);
        if (siteId) {
          const known = (await fetchLocations(siteId))[0] ?? null;
          setLocation(known);
          if (known) {
            setCoords({ latitude: String(known.latitude), longitude: String(known.longitude) });
            setForecast(await fetchForecast(siteId));
          }
        }
      } else if (tab === 'policy') {
        setPolicy((await fetchPolicy(siteId)).policy);
      } else if (tab === 'exports') {
        setDatasets(await fetchDatasets());
        if (canReadExportLog) setLog(await fetchExportLog());
        if (networkWide) setMarts(await fetchMarts());
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }, [tab, period, siteId, networkWide, canReadExportLog]);

  useEffect(() => {
    void load();
  }, [load]);

  async function guard(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModulePage
      title={title}
      tabs={visible.map((item) => ({ key: item.key, label: tr(item.label) }))}
      activeTab={tab}
      onTab={setTab}
      actions={
        <>
          <Segmented
            items={PERIODS.map((p) => ({ key: p.key, label: tr(p.label) }))}
            active={periodKey}
            onSelect={setPeriodKey}
          />
          <Button onClick={() => void load()} disabled={busy}>
            {tr('common.refresh')}
          </Button>
        </>
      }
    >
      {busy ? <LoadingState label={tr('common.loading')} /> : null}

      {tab === 'identification' && dashboard ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile
              label={tr('tile.unidentified')}
              value={percent(dashboard.totals.unidentifiedShare)}
              tone={dashboard.totals.unidentifiedShare > 0.15 ? 'bad' : undefined}
            />
            <Tile label={tr('tile.identified')} value={percent(dashboard.totals.identifiedShare)} />
            <Tile label={tr('tile.visits')} value={String(dashboard.totals.counted)} />
            <Tile label={tr('tile.declined')} value={String(dashboard.totals.declined)} />
            <Tile label={tr('tile.excluded')} value={String(dashboard.totals.serviceExcluded)} />
          </div>

          <Panel title={tr('reason.title')}>
            <DataGrid
              head={[tr('reason.code'), tr('reason.visits'), tr('reason.share')]}
              empty={tr('common.empty')}
              rows={dashboard.byReason.map((r) => [r.label, r.visits, percent(r.shareOfDeclined)])}
            />
          </Panel>

          <Panel title={tr('shift.title')}>
            <DataGrid
              head={[
                tr('shift.opened'),
                tr('common.visits'),
                tr('common.identified'),
                tr('common.share'),
              ]}
              empty={tr('common.empty')}
              rows={dashboard.byShift.map((r) => [
                new Date(r.openedAt).toLocaleString('ru-RU'),
                r.visits,
                r.identified,
                percent(r.identifiedShare),
              ])}
            />
          </Panel>

          <Panel title={tr('operator.title')}>
            <DataGrid
              head={[
                tr('operator.name'),
                tr('common.visits'),
                tr('common.identified'),
                tr('common.share'),
              ]}
              empty={tr('common.empty')}
              rows={dashboard.byOperator.map((r) => [
                r.displayName ?? '—',
                r.visits,
                r.identified,
                percent(r.identifiedShare),
              ])}
            />
          </Panel>

          <Panel title={tr('history.title')}>
            <DataGrid
              head={[
                tr('history.day'),
                tr('common.visits'),
                tr('common.identified'),
                tr('common.share'),
              ]}
              empty={tr('common.empty')}
              rows={dashboard.history.map((r) => [
                r.day,
                r.visits,
                r.identified,
                percent(r.identifiedShare),
              ])}
            />
          </Panel>
        </div>
      ) : null}

      {tab === 'executive' && executive ? (
        <Panel title={tr('tab.executive')}>
          <DataGrid
            head={[
              tr('common.site'),
              tr('common.visits'),
              tr('common.share'),
              tr('common.orders'),
              tr('common.grossRevenue'),
              tr('common.refunds'),
              tr('common.subscriptionRevenue'),
              tr('common.revenue'),
              tr('common.averageCheck'),
            ]}
            empty={tr('common.empty')}
            rows={[
              ...executive.rows.map((r) => [
                r.siteName,
                r.visits,
                percent(r.identifiedShare),
                r.orders,
                money(r.grossRevenueMinor),
                refundCell(r.refundedMinor),
                money(r.subscriptionRevenueMinor),
                money(r.revenueMinor),
                money(r.averageCheckMinor),
              ]),
              [
                <strong key="total">{tr('common.total')}</strong>,
                executive.totals.visits,
                percent(executive.totals.identifiedShare),
                executive.totals.orders,
                money(executive.totals.grossRevenueMinor),
                refundCell(executive.totals.refundedMinor),
                money(executive.totals.subscriptionRevenueMinor),
                money(executive.totals.revenueMinor),
                money(executive.totals.averageCheckMinor),
              ],
            ]}
          />
        </Panel>
      ) : null}

      {tab === 'cohorts' && cohorts ? (
        <Panel title={tr('cohort.retention')}>
          <DataGrid
            head={[
              tr('cohort.month'),
              tr('cohort.size'),
              tr('cohort.visits'),
              tr('cohort.repeatVisits'),
              tr('cohort.repeatShare'),
              tr('cohort.perCustomer'),
              tr('cohort.retention'),
            ]}
            empty={tr('common.empty')}
            rows={cohorts.rows.map((row) => [
              row.cohortMonth,
              row.size,
              row.visits,
              row.repeatVisits,
              percent(row.repeatShare),
              row.visitsPerCustomer,
              row.cells
                .filter((c) => c.monthsSince > 0)
                .map((c) => `+${c.monthsSince}: ${percent(c.retention)}`)
                .join('  ') || '—',
            ])}
          />
        </Panel>
      ) : null}

      {tab === 'subscriptions' && subscriptions ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile label={tr('subscription.activated')} value={String(subscriptions.activated)} />
            <Tile label={tr('subscription.cancelled')} value={String(subscriptions.cancelled)} />
            <Tile label={tr('subscription.active')} value={String(subscriptions.activeAtEnd)} />
            <Tile
              label={tr('subscription.churn')}
              value={percent(subscriptions.churn)}
              tone={subscriptions.churn > 0.1 ? 'warn' : undefined}
            />
            <Tile label={tr('subscription.uses')} value={String(subscriptions.uses)} />
          </div>
          <Panel title={tr('common.site')}>
            <p className="mb-3 text-sm text-ink-muted">{tr('subscription.bySite')}</p>
            <DataGrid
              head={[
                tr('common.site'),
                tr('subscription.activated'),
                tr('subscription.cancelled'),
                tr('subscription.active'),
                tr('subscription.churn'),
                tr('subscription.uses'),
                tr('subscription.subscriptions'),
              ]}
              empty={tr('common.empty')}
              rows={subscriptions.bySite.map((r) => [
                r.siteName,
                r.activated,
                r.cancelled,
                r.activeAtEnd,
                percent(r.churn),
                r.uses,
                r.subscriptions,
              ])}
            />
          </Panel>
        </div>
      ) : null}

      {tab === 'metrics' && metrics ? (
        <Panel title={tr('tab.metrics')}>
          <p className="mb-3 text-sm text-ink-muted">{tr('metric.note')}</p>
          <DataGrid
            head={[
              tr('metric.title'),
              tr('metric.value'),
              tr('metric.formula'),
              tr('metric.source'),
            ]}
            empty={tr('common.empty')}
            rows={metrics.metrics.map((m) => [
              m.definition.titleRu,
              m.definition.unit === 'share'
                ? percent(m.value)
                : m.definition.unit === 'seconds'
                  ? `${m.value} с`
                  : String(m.value),
              m.definition.formula,
              m.definition.source,
            ])}
          />
        </Panel>
      ) : null}

      {tab === 'forecast' ? (
        <div className="flex flex-col gap-4">
          {!siteId ? <p className="text-sm text-ink-muted">{tr('forecast.pickSite')}</p> : null}
          {siteId ? (
            <Panel title={tr('forecast.location')}>
              {!location ? <p className="mb-3 text-sm text-warn">{tr('forecast.noLocation')}</p> : null}
              <div className="flex flex-wrap items-end gap-3">
                <Field label={tr('forecast.latitude')} className="flex-col">
                  <input
                    className="h-8 w-32 rounded-control border border-line-strong bg-surface px-2 text-sm text-ink"
                    value={coords.latitude}
                    disabled={!canAdmin}
                    onChange={(e) => setCoords({ ...coords, latitude: e.target.value })}
                  />
                </Field>
                <Field label={tr('forecast.longitude')} className="flex-col">
                  <input
                    className="h-8 w-32 rounded-control border border-line-strong bg-surface px-2 text-sm text-ink"
                    value={coords.longitude}
                    disabled={!canAdmin}
                    onChange={(e) => setCoords({ ...coords, longitude: e.target.value })}
                  />
                </Field>
                {canAdmin ? (
                  <>
                    <Button
                      disabled={busy}
                      onClick={() =>
                        void guard(async () => {
                          await saveLocation({
                            siteId,
                            latitude: Number(coords.latitude.replace(',', '.')),
                            longitude: Number(coords.longitude.replace(',', '.')),
                          });
                          await syncWeather(siteId);
                          await load();
                        })
                      }
                    >
                      {tr('forecast.saveLocation')}
                    </Button>
                    {location ? (
                      <Button
                        disabled={busy}
                        onClick={() =>
                          void guard(async () => {
                            await syncWeather(siteId);
                            await load();
                          })
                        }
                      >
                        {tr('forecast.sync')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </Panel>
          ) : null}

          {forecast ? (
            <>
              <Panel title={tr('forecast.quality')}>
                <p className="text-sm text-ink">
                  {tr('forecast.qualityNote')
                    .replace('{holdout}', String(forecast.model.holdoutDays))
                    .replace('{mape}', forecast.model.mape === null ? '—' : percent(forecast.model.mape))
                    .replace(
                      '{baseline}',
                      forecast.model.baselineMape === null ? '—' : percent(forecast.model.baselineMape),
                    )
                    .replace('{days}', String(forecast.model.trainingDays))}
                </p>
              </Panel>
              <Panel title={tr('tab.forecast')}>
                <DataGrid
                  head={[
                    tr('forecast.day'),
                    tr('forecast.weather'),
                    tr('forecast.expected'),
                    tr('forecast.range'),
                    tr('forecast.calendar'),
                  ]}
                  empty={tr('common.empty')}
                  rows={forecast.days.map((d) => [
                    `${d.day}, ${d.weekday}`,
                    d.weather
                      ? `${d.weather.tempMax ?? '—'}° / ${d.weather.tempMin ?? '—'}°, осадки ${d.weather.precipitationMm} мм${
                          d.weather.snowfallCm > 0 ? `, снег ${d.weather.snowfallCm} см` : ''
                        }`
                      : '—',
                    <strong key={d.day}>{Math.round(d.expected)}</strong>,
                    `${Math.round(d.low)}–${Math.round(d.high)}`,
                    Math.round(d.weekdayMean),
                  ])}
                />
              </Panel>
              <Panel title={tr('forecast.recent')}>
                <DataGrid
                  head={[tr('forecast.day'), tr('forecast.actual'), tr('forecast.predicted')]}
                  empty={tr('common.empty')}
                  rows={forecast.recent.map((r) => [r.day, r.actual, Math.round(r.predicted)])}
                />
              </Panel>
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'policy' && policy ? (
        <Panel title={tr('policy.title')}>
          <p className="mb-4 text-sm text-warn">{tr('policy.note')}</p>
          <div className="flex flex-col gap-4">
            <Field label={tr('policy.rule')} className="flex-col">
              <Select
                value={policy.rule}
                onChange={(event) => setPolicy({ ...policy, rule: event.target.value })}
                className="max-w-md"
              >
                <option value="linked">{tr('policy.rule.linked')}</option>
                <option value="verified_phone">{tr('policy.rule.verified_phone')}</option>
                <option value="consented">{tr('policy.rule.consented')}</option>
              </Select>
            </Field>

            <label className="flex items-center gap-2 text-sm text-ink">
              <Checkbox
                checked={policy.excludeServiceVehicles}
                onChange={(event) =>
                  setPolicy({ ...policy, excludeServiceVehicles: event.target.checked })
                }
              />
              {tr('policy.excludeService')}
            </label>

            <div>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void guard(async () => {
                    const saved = await savePolicy({
                      siteId: siteId ?? null,
                      rule: policy.rule,
                      excludeServiceVehicles: policy.excludeServiceVehicles,
                    });
                    setPolicy(saved);
                    setNotice(tr('policy.saved'));
                  })
                }
                className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
              >
                {tr('policy.save')}
              </button>
            </div>
          </div>
        </Panel>
      ) : null}

      {tab === 'exports' ? (
        <div className="flex flex-col gap-4">
          <Panel title={tr('export.title')}>
            <p className="mb-3 text-sm text-ink-muted">{tr('export.hint')}</p>
            <p className="mb-3 font-mono text-xs text-ink-muted">{tr('export.api')}</p>
            <DataGrid
              head={[tr('export.dataset'), tr('export.kind'), tr('export.pii'), '']}
              empty={tr('common.empty')}
              rows={datasets
                .filter((d) => networkWide || !d.networkOnly)
                .map((d) => [
                d.titleRu,
                tr(d.kind === 'rows' ? 'export.kind.rows' : 'export.kind.mart'),
                d.containsPii ? 'да' : 'нет',
                <span key={d.id} className="flex gap-2">
                  {(['csv', 'json'] as const).map((format) => (
                    <button
                      key={format}
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void guard(async () => {
                          const result = await runExport({
                            dataset: d.id,
                            format,
                            from: period.from,
                            to: period.to,
                            ...(siteId ? { siteId } : {}),
                          });
                          // Данные наконец доходят до человека, а не только до журнала.
                          downloadText(
                            exportFilename(d.id, format, period.from, period.to),
                            result.content,
                            EXPORT_MIME[format] ?? 'text/plain',
                          );
                          setNotice(
                            `${d.titleRu}: ${countWithNoun(result.rowCount, ['строка', 'строки', 'строк'])}${
                              result.truncated ? tr('export.truncated') : ''
                            }`,
                          );
                          if (canReadExportLog) setLog(await fetchExportLog());
                        })
                      }
                      className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                </span>,
              ])}
            />
          </Panel>

          {networkWide ? (
            <Panel title={tr('marts.title')}>
              <p className="mb-3 text-sm text-ink-muted">{tr('marts.note')}</p>
              <DataGrid
                head={[tr('marts.view'), tr('marts.populated'), tr('marts.rows')]}
                empty={tr('common.empty')}
                rows={marts.map((m) => [m.view, m.populated ? 'да' : 'нет', m.rows])}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void guard(async () => {
                    await refreshMarts();
                    setMarts(await fetchMarts());
                    setNotice(tr('marts.title'));
                  })
                }
                className="mt-4 inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
              >
                {tr('marts.refresh')}
              </button>
            </Panel>
          ) : null}

          {canReadExportLog ? (
            <Panel title={tr('export.log')}>
              <DataGrid
                head={[tr('export.when'), tr('export.scope'), tr('export.rows'), tr('export.pii')]}
                empty={tr('common.empty')}
                rows={log.map((entry) => [
                  new Date(entry.createdAt).toLocaleString('ru-RU'),
                  entry.scope,
                  entry.rowCount,
                  entry.containsPii ? 'да' : 'нет',
                ])}
              />
            </Panel>
          ) : null}
        </div>
      ) : null}
    </ModulePage>
  );
}
